import { Outlet, useLoaderData, useRevalidator } from "@remix-run/react";
import { json } from "@remix-run/node";
import newrelic from "newrelic";
import { useEffect, useState } from "react";

import { env } from "~/utils/helpers";
import { getAssetUrl, getBpComponentsUrl } from "~/utils/url";

import pkg from "../package.json";
import { HostContext } from "./constants";
import { getLabels } from "./models/labels.server";
import { getCurrentUser } from "./models/user.server";
import { getGlobalElements } from "./models/external.server";
import { I18nLocale } from "./components/i18n/context";
import { preferencesFromCookie } from "./utils/preferences";
import { sendMessageToHost } from "./utils/events";
import { useAuthStateChange } from "./hooks/useAuthStateChange";
import { usePatchedMethods } from "./hooks/usePatchedMethods";
import { useBpComponentEvents } from "./hooks/useBpComponentEvents";
import { useMountEffect } from "./hooks/useMountEffect";
import { UserContextProvider } from "./components/userContext/UserContextProvider";
import { LoadingOverlay } from "./components/loadingOverlay/LoadingOverlay";

import type { User } from "./graphql/types";
import type { LoaderFunctionArgs } from "@remix-run/node";
import type { UserPreferences } from "./components/userContext/context";
import type { I18nDict } from "./components/i18n/context";
import type { WebUIServiceResponse } from "./models/external.server";

export interface RootLoaderData {
  /** The current user, if logged-in */
  currentUser: Maybe<Partial<User>>;
  /** Environment variables used at runtime */
  env: Partial<EnvironmentVariables>;
  /** The context in which the app was loaded  */
  hostContext: HostContext;
  /** Whether or not the requested page is a stencil route */
  isStencil: boolean;
  /** Labels used for i18n */
  labels: I18nDict;
  /** The user's locale */
  locale: I18nLocale;
  /** New Relic browser monitoring script */
  nr?: string;
  /** User preferences loaded from a cookie */
  preferences: Maybe<UserPreferences>;
  /** Global header data */
  header?: WebUIServiceResponse;
  /** Global footer data */
  footer?: WebUIServiceResponse;
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const [labels, currentUser] = await Promise.all([
    getLabels(context.graphql),
    getCurrentUser(context.graphql).catch(() => null),
  ]);

  const preferences = preferencesFromCookie(
    request.headers.get("Cookie") ?? "",
  );

  const { header, footer } = await getGlobalElements({
    hostContext: context.hostContext,
    referer: request.url,
  });

  return json<RootLoaderData>({
    currentUser,
    env: {
      API_URL: env("API_URL"),
      BP_COMPONENTS_API_URL: env("BP_COMPONENTS_API_URL"),
      BP_COMPONENTS_USER_API_URL: env("BP_COMPONENTS_USER_API_URL"),
      ENABLE_APP_DOWNLOAD_LANDING_PAGE: env("ENABLE_APP_DOWNLOAD_LANDING_PAGE"),
      ENABLE_SHARE_BUTTON: env("ENABLE_SHARE_BUTTON"),
      ENABLE_DATA_TRANSFER: env("ENABLE_DATA_TRANSFER"),
      GTM_CONTAINER_ID: env("GTM_CONTAINER_ID"),
      HOST: env("HOST"),
      NODE_ENV: env("NODE_ENV"),
      PORT: env("PORT"),
      SEARCH_API_TOKEN: env("SEARCH_API_TOKEN"),
      SEARCH_API_URL: env("SEARCH_API_URL"),
      VERSION: pkg.version,
    },
    hostContext: context.hostContext,
    isStencil: request.url.includes("/_stencils"),
    labels,
    locale: I18nLocale.EN,
    nr: newrelic.getBrowserTimingHeader({
      hasToRemoveScriptWrapper: true,
    }),
    preferences,
    header,
    footer,
  });
}

export function links() {
  return [
    { rel: "icon", href: getAssetUrl("/favicon.ico") },
    { rel: "icon", href: getAssetUrl("/favicon.svg"), type: "image/svg+xml" },
    { rel: "apple-touch-icon", href: getAssetUrl("/apple-touch-icon.png") },
    { rel: "stylesheet", href: getBpComponentsUrl("bp.css") },
    { rel: "stylesheet", href: getAssetUrl("/global.css") },
  ];
}

export { Layout } from "./Layout";
export { ErrorBoundary } from "./ErrorBoundary";

export default function App() {
  const data = useLoaderData<typeof loader>();
  const revalidator = useRevalidator();
  const [isAuthUpdating, setIsAuthUpdating] = useState(false);

  // Monkey patch any methods that need to be patched
  usePatchedMethods();

  // Register event listeners for bp-web-components
  useBpComponentEvents();

  // Let the host know that the UI is ready
  useMountEffect(() => {
    sendMessageToHost({ ui: { ready: {} } });
  });

  // Listen for auth state changes. Revalidate the page when
  // the user's auth state changes.
  useAuthStateChange({
    authorized: Boolean(data?.currentUser),
    onChange() {
      setIsAuthUpdating(true);
      revalidator.revalidate();
    },
  });

  // Listen to revalidator state changes to signal that the
  // auth state change and side effects have completed
  useEffect(() => {
    if (revalidator.state === "idle") {
      setIsAuthUpdating(false);
    }
  }, [revalidator.state]);

  return (
    <UserContextProvider
      currentUser={data?.currentUser}
      hostContext={data?.hostContext ?? HostContext.Unknown}
      defaultPreferences={data?.preferences}
    >
      <Outlet />
      {isAuthUpdating && <LoadingOverlay />}
    </UserContextProvider>
  );
}
